import type { CompanyDocumentDto, CompanyKycDraftDto, SaveCompanyKycRequest } from "@/services/CompanyOnboardingService";
import type { CatalogOption } from "@/screen/Companies/CompanyDetails/companyDetailsForm";
import { MaxDocumentBytes } from "@/screen/Companies/CompanyDetails/companyDetailsForm";

// Literales de Omnia (FID.Web, CompanyOnboarding/AltoBlock): se envían tal cual en el alta de la empresa
export const EconomicActivityOptions: CatalogOption[] =
[
    { value: "No", text: "No" },
    { value: "Si (en activo)", text: "Sí (en activo)" },
    { value: "Si (retirado)", text: "Sí (retirado)" },
];

export const FundsOriginInvestments = "Inversiones (especificar)";
export const FundsOriginOther = "Otros (especificar)";

export const FundsOriginOptions: CatalogOption[] =
[
    { value: "Beneficios ordinarios", text: "Beneficios ordinarios" },
    { value: "Dividendos", text: "Dividendos" },
    { value: "Venta de propiedades", text: "Venta de propiedades" },
    { value: "Venta de activos", text: "Venta de activos" },
    { value: FundsOriginInvestments, text: FundsOriginInvestments },
    { value: FundsOriginOther, text: FundsOriginOther },
];

export const AnnualGrossIncomeOptions: CatalogOption[] =
[
    { value: "Sin ingresos", text: "Sin ingresos" },
    { value: "Inferiores a 60.000€", text: "Inferiores a 60.000 €" },
    { value: "Entre 60.000€ y 90.000€", text: "Entre 60.000 € y 90.000 €" },
    { value: "Entre 90.001€ y 120.000€", text: "Entre 90.001 € y 120.000 €" },
    { value: "Entre 120.001€ y 150.000€", text: "Entre 120.001 € y 150.000 €" },
    { value: "Entre 150.001€ y 500.000€", text: "Entre 150.001 € y 500.000 €" },
    { value: "Más de 500.000€", text: "Más de 500.000 €" },
];

/** Formulario del paso 4. Las preguntas Sí/No son booleanos; los importes, texto del input hasta enviarlos. */
export interface CompanyKycFormData
{
    isPep: boolean;
    pepFullName: string;
    pepPosition: string;
    isPepRelative: boolean;
    pepRelativeFullName: string;
    pepRelativePosition: string;
    pepRelativeRelationship: string;
    riskCountries: boolean;
    riskCountriesWhich: string[];
    listedCompany: boolean;
    isBranchOrSubsidiary: boolean;
    bearerShares: boolean;
    economicActivity: string;
    fundsOrigin: string[];
    fundsOriginInvestmentsWhich: string;
    fundsOriginOther: string;
    annualGrossIncome: string;
    portfolioVolume: string;
    maxContribution: string;
    providesFundsDocumentation: boolean;
    knowsManager: boolean;
    relationshipOpeningReasons: string;
    usCitizen: boolean;
    usPersonControl: boolean;
    usResidence: boolean;
    usTaxObligation: boolean;
    usBeneficiary: boolean;
    usTin: string;
    dataTruthfulnessAccepted: boolean;
}

export type CompanyKycYesNoField = "isPep" | "isPepRelative" | "riskCountries" | "listedCompany" | "isBranchOrSubsidiary" | "bearerShares"
    | "providesFundsDocumentation" | "knowsManager" | "usCitizen" | "usPersonControl" | "usResidence" | "usTaxObligation" | "usBeneficiary";

export type CompanyKycTextField = "pepFullName" | "pepPosition" | "pepRelativeFullName" | "pepRelativePosition" | "pepRelativeRelationship"
    | "economicActivity" | "fundsOriginInvestmentsWhich" | "fundsOriginOther" | "annualGrossIncome" | "portfolioVolume" | "maxContribution"
    | "relationshipOpeningReasons" | "usTin";

// Campos que se marcan en rojo si faltan (los textos y las dos listas)
export type CompanyKycField = CompanyKycTextField | "fundsOrigin" | "riskCountriesWhich";

const text = (value: string | null | undefined): string => value ?? "";
const amount = (value: number | null | undefined): string => (value === null || value === undefined ? "" : String(value));

export function toCompanyKycFormData(draft: CompanyKycDraftDto): CompanyKycFormData
{
    return{
        isPep: draft.isPep === true,
        pepFullName: text(draft.pepFullName),
        pepPosition: text(draft.pepPosition),
        isPepRelative: draft.isPepRelative === true,
        pepRelativeFullName: text(draft.pepRelativeFullName),
        pepRelativePosition: text(draft.pepRelativePosition),
        pepRelativeRelationship: text(draft.pepRelativeRelationship),
        riskCountries: draft.riskCountries === true,
        riskCountriesWhich: draft.riskCountriesWhich ?? [],
        listedCompany: draft.listedCompany === true,
        isBranchOrSubsidiary: draft.isBranchOrSubsidiary === true,
        bearerShares: draft.bearerShares === true,
        economicActivity: text(draft.economicActivity),
        fundsOrigin: draft.fundsOrigin ?? [],
        fundsOriginInvestmentsWhich: text(draft.fundsOriginInvestmentsWhich),
        fundsOriginOther: text(draft.fundsOriginOther),
        annualGrossIncome: text(draft.annualGrossIncome),
        portfolioVolume: amount(draft.portfolioVolume),
        maxContribution: amount(draft.maxContribution),
        providesFundsDocumentation: draft.providesFundsDocumentation === true,
        knowsManager: draft.knowsManager === true,
        relationshipOpeningReasons: text(draft.relationshipOpeningReasons),
        usCitizen: draft.usCitizen === true,
        usPersonControl: draft.usPersonControl === true,
        usResidence: draft.usResidence === true,
        usTaxObligation: draft.usTaxObligation === true,
        usBeneficiary: draft.usBeneficiary === true,
        usTin: text(draft.usTin),
        dataTruthfulnessAccepted: draft.dataTruthfulnessAccepted === true,
    };
}

const clean = (value: string): string | null => (value.trim() === "" ? null : value.trim());

const toAmount = (value: string): number | null =>
{
    const trimmed = value.trim();
    if(trimmed === "")
    {
        return null;
    }
    const parsed = Number(trimmed.replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
};

/** Petición del PUT. Lo que depende de una respuesta que ya no aplica (PEP en No, opción sin marcar...) se envía vacío. */
export function buildSaveCompanyKycRequest(data: CompanyKycFormData, complete: boolean): SaveCompanyKycRequest
{
    const hasInvestments = data.fundsOrigin.includes(FundsOriginInvestments);
    const hasOther = data.fundsOrigin.includes(FundsOriginOther);

    return{
        isPep: data.isPep,
        pepFullName: data.isPep ? clean(data.pepFullName) : null,
        pepPosition: data.isPep ? clean(data.pepPosition) : null,
        isPepRelative: data.isPepRelative,
        pepRelativeFullName: data.isPepRelative ? clean(data.pepRelativeFullName) : null,
        pepRelativePosition: data.isPepRelative ? clean(data.pepRelativePosition) : null,
        pepRelativeRelationship: data.isPepRelative ? clean(data.pepRelativeRelationship) : null,
        riskCountries: data.riskCountries,
        riskCountriesWhich: data.riskCountries ? data.riskCountriesWhich : [],
        listedCompany: data.listedCompany,
        isBranchOrSubsidiary: data.isBranchOrSubsidiary,
        bearerShares: data.bearerShares,
        economicActivity: clean(data.economicActivity),
        fundsOrigin: data.fundsOrigin,
        fundsOriginInvestmentsWhich: hasInvestments ? clean(data.fundsOriginInvestmentsWhich) : null,
        fundsOriginOther: hasOther ? clean(data.fundsOriginOther) : null,
        annualGrossIncome: clean(data.annualGrossIncome),
        portfolioVolume: toAmount(data.portfolioVolume),
        maxContribution: toAmount(data.maxContribution),
        providesFundsDocumentation: data.providesFundsDocumentation,
        knowsManager: data.knowsManager,
        relationshipOpeningReasons: clean(data.relationshipOpeningReasons),
        usCitizen: data.usCitizen,
        usPersonControl: data.usPersonControl,
        usResidence: data.usResidence,
        usTaxObligation: data.usTaxObligation,
        usBeneficiary: data.usBeneficiary,
        usTin: clean(data.usTin),
        dataTruthfulnessAccepted: data.dataTruthfulnessAccepted,
        complete,
    };
}

/** Campos obligatorios que faltan para enviar a bastanteo, con el mismo criterio que el backend. */
export function getMissingKycFields(data: CompanyKycFormData): CompanyKycField[]
{
    const missing: CompanyKycField[] = [];
    const isBlank = (value: string): boolean => value.trim() === "";

    if(data.isPep)
    {
        if(isBlank(data.pepFullName))
        {
            missing.push("pepFullName");
        }
        if(isBlank(data.pepPosition))
        {
            missing.push("pepPosition");
        }
    }
    if(data.isPepRelative)
    {
        if(isBlank(data.pepRelativeFullName))
        {
            missing.push("pepRelativeFullName");
        }
        if(isBlank(data.pepRelativePosition))
        {
            missing.push("pepRelativePosition");
        }
        if(isBlank(data.pepRelativeRelationship))
        {
            missing.push("pepRelativeRelationship");
        }
    }
    if(data.riskCountries && data.riskCountriesWhich.length === 0)
    {
        missing.push("riskCountriesWhich");
    }
    if(isBlank(data.economicActivity))
    {
        missing.push("economicActivity");
    }
    if(data.fundsOrigin.length === 0)
    {
        missing.push("fundsOrigin");
    }
    if(data.fundsOrigin.includes(FundsOriginInvestments) && isBlank(data.fundsOriginInvestmentsWhich))
    {
        missing.push("fundsOriginInvestmentsWhich");
    }
    if(data.fundsOrigin.includes(FundsOriginOther) && isBlank(data.fundsOriginOther))
    {
        missing.push("fundsOriginOther");
    }
    if(isBlank(data.annualGrossIncome))
    {
        missing.push("annualGrossIncome");
    }
    if(isBlank(data.relationshipOpeningReasons))
    {
        missing.push("relationshipOpeningReasons");
    }
    if(toAmount(data.portfolioVolume) !== null && (toAmount(data.portfolioVolume) as number) < 0)
    {
        missing.push("portfolioVolume");
    }
    if(toAmount(data.maxContribution) !== null && (toAmount(data.maxContribution) as number) < 0)
    {
        missing.push("maxContribution");
    }
    return missing;
}

/** Lo que falta para enviar a bastanteo además de los campos: pruebas de ingresos y declaración. */
export function getKycIssues(data: CompanyKycFormData, documents: CompanyDocumentDto[]): string[]
{
    const issues: string[] = [];
    if(getMissingKycFields(data).length > 0)
    {
        issues.push("Completa los campos obligatorios marcados.");
    }
    if(!documents.some((document) => document.category === "ProofOfIncome"))
    {
        issues.push("Adjunta al menos un documento de pruebas de ingresos.");
    }
    if(!data.dataTruthfulnessAccepted)
    {
        issues.push("Debes declarar que los datos facilitados son veraces y están actualizados.");
    }
    return issues;
}

/** Las pruebas de ingresos solo se admiten en PDF (maqueta). Mensaje si el fichero no vale; null si vale. */
export function getProofOfIncomeFileError(file: File): string | null
{
    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    if(extension !== "pdf")
    {
        return "Las pruebas de ingresos deben ser documentos PDF.";
    }
    return file.size > MaxDocumentBytes ? "El documento no puede superar los 10 MB." : null;
}
